import { useState } from 'react';
import { documentName, fill, richTextIsEmpty, t, termsFromClauses, termsRichText } from '@bms2/shared';
import { Checkbox, RichTextEditor, RichTextView } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { put, useApi } from './api';
import styles from './DocumentTermsModal.module.css';
import { useDayText } from '../../shell/viewerDates';

/** 🔨 Relative to the API's own root, never prefixed with /api. */
export const DOCUMENT_TERMS_PATH = '/document-terms';

export function documentTermsPath(documentType: string, documentId: string): string {
  return `${DOCUMENT_TERMS_PATH}/${encodeURIComponent(documentType)}/${encodeURIComponent(documentId)}`;
}

/** 🔒 One frozen copy, which a version list's entry opens (T277; decision 0773). */
export function documentCopyPath(documentType: string, documentId: string, versionNumber: number): string {
  return `${documentTermsPath(documentType, documentId)}/versions/${versionNumber}`;
}

/** One clause in force, as the server resolved it through the cascade and the clause's own scope. */
export interface DocumentTermsClause {
  id: string;
  position: string;
  title: string;
  body: string;
  /** 🔒 The heading of the section the clause prints beneath (decision 0702); null on the Preamble. */
  heading: string | null;
  /** 🔒 The sub-section's heading inside that section (decision 0771); null or absent where the clause stands in the section itself. */
  subHeading?: string | null;
  /** 🔒 True on a clause a confidentiality role added, which prints after the document's own text as well (decision 1171). */
  addition?: boolean;
}

/** The server's answer, typed against what the document terms route sends. */
export interface DocumentTermsAnswer {
  documentType: string;
  documentId: string;
  clauses: DocumentTermsClause[];
  termsOverride: string | null;
  editable: boolean;
  frozenReason: string | null;
  /** 🔒 Where the clauses are the ones the document went out with (T277; decision 0773); null or absent on a Draft. */
  asSent?: { sentAt: string; versionNumber: number } | null;
  /** 🔒 True where the latest copy predates the kept terms, so the clauses are today's. */
  predatesKeptTerms?: boolean;
  /** 🔒 The terms set the clauses come from and why (decision 1171); null or absent where the whole library prints. */
  set?: { id: string; name: string; from: 'Client' | 'Class' | 'Library' } | null;
}

/** 🔒 The line naming the set the clauses come from (decision 1171): the client's choice or the class default; none where the whole library prints. */
export function termsSetLine(set: DocumentTermsAnswer['set']): string | null {
  if (!set || set.from === 'Library') return null;
  return fill(t(set.from === 'Client' ? 'document.terms.fromClientSet' : 'document.terms.fromClassSet'), { name: set.name });
}

export const documentTermsModalDocumentedStates = ['clauses', 'replaced', 'frozen'] as const;

export interface DocumentTermsModalProps {
  documentType: string;
  documentId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** After a save, so the page reads its sheet again. */
  onSaved: () => void;
}

/**
 * 🔒 The Terms window on a printed document's page (piece 22; T125, decision
 * 0684): the clauses in force for this document, and the choice to replace the
 * text for this document alone. The replacement opens on the clauses as the
 * sheet prints them, so changing one clause is an edit and not a retyping.
 *
 * 🔨 It is mounted only while open, so a second opening reads the document
 * again rather than the draft the first one left.
 */
export function DocumentTermsModal({ documentType, documentId, apiBaseUrl = '', onCancel, onSaved }: DocumentTermsModalProps) {
  const { state } = useApi<DocumentTermsAnswer>(documentTermsPath(documentType, documentId), apiBaseUrl);
  const answer = state.kind === 'ready' ? state.data : null;
  return answer === null ? (
    <Modal open title={t('document.terms.title')} subtitle={documentName(documentType)} onCancel={onCancel} dataModal="document-terms" actions={[
      // 🔒 Decision 2140: the terms read here are not written, so the window carries one Close (decision 3063).
      { kind: 'close', label: t('modal.close'), onAct: onCancel },
    ]}>
      <p className={styles.muted}>{state.kind === 'loading' ? t('document.terms.loading') : t('document.terms.unavailable')}</p>
    </Modal>
  ) : (
    <DocumentTermsForm key={answer.termsOverride ?? ''} answer={answer} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onSaved={onSaved} />
  );
}

/**
 * 🔒 The line saying the terms listed are the ones the document went out with
 * (T277; decision 0773), the day in the viewer's own zone through the one zone
 * helper; or, on a copy that predates the kept terms, that they are today's.
 */
export function TermsAsSentLine({ sentAt, predates = false }: { sentAt: string; predates?: boolean }) {
  const dayText = useDayText();
  const date = dayText(sentAt);
  return <p className={styles.asSent} data-document-terms-as-sent={predates ? 'predates' : 'kept'}>{fill(t(predates ? 'document.terms.predates' : 'document.terms.asSent'), { date })}</p>;
}

/** The clauses as the window lists them: each section's heading, a sub-section's inside it, then each clause's place, title and words (decisions 0702, 0771). */
export function TermsClauseList({ clauses }: { clauses: readonly DocumentTermsClause[] }) {
  return (
    <ol className={styles.clauses} data-document-terms-clauses={clauses.length}>
      {clauses.map((clause, index) => (
        <li key={clause.id} className={styles.clause} data-document-terms-clause={clause.id}>
          {/* 🔒 A section's heading before its first clause (decision 0702). */}
          {clause.heading !== null && clause.heading !== (clauses[index - 1]?.heading ?? null) ? <span className={styles.sectionHeading} data-document-terms-heading={clause.heading}>{clause.heading}</span> : null}
          {/* 🔒 A sub-section's heading before its first clause, inside its section (decision 0771). */}
          {clause.subHeading && clause.subHeading !== (clauses[index - 1]?.subHeading ?? null) ? <span className={styles.subHeading} data-document-terms-subheading={clause.subHeading}>{clause.subHeading}</span> : null}
          <span className={styles.clauseTitle}>{clause.position ? `${clause.position} ` : ''}{clause.title}</span>
          {/* 🔒 A clause a confidentiality role added says so: nothing on this window removes it (decision 1171). */}
          {clause.addition ? <span className={styles.addition} data-document-terms-addition="true">{t('document.terms.addition')}</span> : null}
          <RichTextView html={clause.body} data-rich-text-view="document-terms-clause" />
        </li>
      ))}
    </ol>
  );
}

function DocumentTermsForm({ answer, apiBaseUrl, onCancel, onSaved }: { answer: DocumentTermsAnswer; apiBaseUrl: string; onCancel: () => void; onSaved: () => void }) {
  const [replacing, setReplacing] = useState(answer.termsOverride !== null);
  // 🔒 The replacement opens on the clauses as the sheet prints them, as rich text (decision 0704).
  // 🔒 The clauses a role added print after the own text anyway, so the replacement never opens on them (decision 1171).
  const [text, setText] = useState(answer.termsOverride ?? termsRichText(termsFromClauses(answer.clauses.filter((clause) => !clause.addition))));
  const setLine = termsSetLine(answer.set);
  const hasAdditions = answer.clauses.some((clause) => clause.addition);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const outcome = await put<DocumentTermsAnswer>(documentTermsPath(answer.documentType, answer.documentId), { termsOverride: replacing ? text : null }, apiBaseUrl);
    setSaving(false);
    if (!outcome.ok) return setError(outcome.message);
    onSaved();
  };

  const actions = answer.editable
    ? [
        { kind: 'primary' as const, label: t('modal.save'), onAct: () => void save(), disabled: saving || (replacing && richTextIsEmpty(text)) },
        { kind: 'cancel' as const, label: t('modal.cancel'), onAct: onCancel },
      ]
    : [
        // 🔒 Decision 2140: read only, one Close (decision 3063).
        { kind: 'close' as const, label: t('modal.close'), onAct: onCancel },
      ];

  return (
    <Modal open width="wide" title={t('document.terms.title')} subtitle={documentName(answer.documentType)} onCancel={onCancel} dataModal="document-terms" actions={actions}>
      <div className={styles.body} data-document-terms={answer.documentType} data-document-terms-replaced={answer.termsOverride === null ? 'false' : 'true'}>
        {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        {answer.frozenReason ? <p className={styles.frozen} data-document-terms-frozen="true">{answer.frozenReason}</p> : null}
        {answer.asSent ? <TermsAsSentLine sentAt={answer.asSent.sentAt} predates={answer.predatesKeptTerms === true} /> : null}
        <h3 className={styles.heading}>{t('document.terms.inForce')}</h3>
        {setLine ? <p className={styles.muted} data-document-terms-set={answer.set?.from ?? ''}>{setLine}</p> : null}
        {hasAdditions ? <p className={styles.muted} data-document-terms-additions="true">{t('document.terms.additionHint')}</p> : null}
        {answer.clauses.length === 0 ? <p className={styles.muted}>{t('document.terms.none')}</p> : <TermsClauseList clauses={answer.clauses} />}
        {answer.editable ? (
          <>
            <Checkbox label={t('document.terms.replace')} checked={replacing} onChange={setReplacing} name="documentTermsReplace" />
            {replacing ? (
              <>
                <RichTextEditor textOnly label={t('document.terms.ownText')} value={text} onChange={setText} name="documentTermsText" />
                <p className={styles.muted}>{t('document.terms.ownHint')}</p>
              </>
            ) : null}
          </>
        ) : answer.termsOverride !== null ? (
          <>
            <h3 className={styles.heading}>{t('document.terms.ownText')}</h3>
            <div className={styles.own} data-document-terms-own="true"><RichTextView html={answer.termsOverride} /></div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
